import { useEffect } from 'react'

import {
  AlbumTypeEnum,
  albumByIdQuery,
  albumCreate,
  albumTypeArray,
  albumUpdate,
  getAlbumTypeString,
} from '@/entities/albums'
import { type AuthorPreview } from '@/entities/authors'
import { type UploadFileWithId, UploadPicture } from '@/features/upload-files'
import {
  hasFormChanged,
  parseDateStringToDayjsDate,
} from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { CopyButton, HidingSpin, SelfValidateInput } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  DatePicker,
  Flex,
  Form,
  Select,
  Space,
  Spin,
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { MAX_FIELD_LENGTH } from '../lib/constants'
import { getAlbumRequestByFormValues } from '../lib/get-album-by-form'
import {
  $albumCommonInformationFormAuthors,
  $albumCommonInformationFormCoauthors,
  $authorsAlbumCommonInformationFormSearch,
  $coauthorsAlbumCommonInformationFormSearch,
  $isAlbumCommonInformationFormDataPending,
  AlbumCommonInformationFormGate,
  albumCommonInformationFormAlbumDataChanged,
  authorsAlbumCommonInformationFormPageIncreased,
  authorsAlbumCommonInformationFormQuery,
  authorsAlbumCommonInformationFormSearchChanged,
  authorsAlbumCommonInformationFormSearchReset,
  coauthorsAlbumCommonInformationFormPageIncreased,
  coauthorsAlbumCommonInformationFormQuery,
  coauthorsAlbumCommonInformationFormSearchChanged,
  coauthorsAlbumCommonInformationFormSearchReset,
} from '../model/album-form-common-information.model'

const albumSchema = z.object({
  name: z.string().min(1, 'Введите название'),
  authorId: z.string().min(1, 'Выберите автора'),
  coauthorIds: z.array(z.string()).optional(),
  type: z.nativeEnum(AlbumTypeEnum).optional(),
  releaseDate: z.custom<Dayjs>().nullable(),
  pictureFile: z.custom<UploadFileWithId>().nullable(),
  excludeSearch: z.boolean(),
  tagIds: z.array(z.number()).optional(),
})

export type AlbumFormCommonInformationModel = z.infer<typeof albumSchema>

interface AlbumFormCommonInformationProps {
  onAuthorButtonClick: (callback: (id: string) => void) => void
  initialAuthor?: AuthorPreview
}

export const AlbumFormCommonInformation = ({
  onAuthorButtonClick,
  initialAuthor,
}: AlbumFormCommonInformationProps) => {
  // Form data
  const album = useUnit(albumByIdQuery)
  const albumQueryStatus = useUnit(albumByIdQuery.$status)
  const formDataPending = useUnit($isAlbumCommonInformationFormDataPending)
  const changeAlbumData = useUnit(albumCommonInformationFormAlbumDataChanged)
  useEffect(() => {
    if (album.data) {
      changeAlbumData(album.data)
    }
  }, [album, changeAlbumData])

  // Authors
  const authors = useUnit(authorsAlbumCommonInformationFormQuery)
  const authorSearchQuery = useUnit($authorsAlbumCommonInformationFormSearch)
  const changeAuthorsSearch = useUnit(
    authorsAlbumCommonInformationFormSearchChanged,
  )
  const resetAuthorsSearch = useUnit(
    authorsAlbumCommonInformationFormSearchReset,
  )
  const authorsPageIncreased = useUnit(
    authorsAlbumCommonInformationFormPageIncreased,
  )
  const authorsData = useUnit($albumCommonInformationFormAuthors)

  // Coauthors
  const coauthors = useUnit(coauthorsAlbumCommonInformationFormQuery)
  const coauthorSearch = useUnit($coauthorsAlbumCommonInformationFormSearch)
  const changeCoauthorsSearch = useUnit(
    coauthorsAlbumCommonInformationFormSearchChanged,
  )
  const resetCoauthorsSearch = useUnit(
    coauthorsAlbumCommonInformationFormSearchReset,
  )
  const coauthorsPageIncreased = useUnit(
    coauthorsAlbumCommonInformationFormPageIncreased,
  )
  const coauthorsData = useUnit($albumCommonInformationFormCoauthors)

  const createAlbum = useUnit(albumCreate)
  const updateAlbum = useUnit(albumUpdate)

  const submitting = createAlbum.pending || updateAlbum.pending

  const formik = useFormik<AlbumFormCommonInformationModel>({
    initialValues: album.data
      ? {
          name: album.data.name,
          authorId: album.data.authors.filter((a) => !a.isCoauthor)[0].id,
          coauthorIds: album.data.authors
            .filter((a) => a.isCoauthor)
            .map((a) => a.id),
          type: album.data.type,
          releaseDate: parseDateStringToDayjsDate(album.data.releaseDate || ''),
          pictureFile: album.data.picture
            ? ({
                id: album.data.picture.id,
                uid: album.data.picture.id,
                name: album.data.picture.name,
                status: 'done',
                url: album.data.picture.fullCdnUrl,
              } as UploadFileWithId)
            : null,
          excludeSearch: album.data.excludeSearch,
          tagIds: [],
        }
      : {
          name: '',
          authorId: initialAuthor?.id || '',
          coauthorIds: [],
          type: undefined,
          releaseDate: null,
          pictureFile: null,
          excludeSearch: false,
          tagIds: [],
        },
    validationSchema: toFormikValidationSchema(albumSchema),

    onSubmit: (values: AlbumFormCommonInformationModel, { setSubmitting }) => {
      if (!values.type) {
        void showNotificationWarningFx({ message: 'Тип альбома не выбран' })
      }
      if (!values.pictureFile || values.pictureFile.status !== 'done') {
        void showNotificationWarningFx({ message: 'Обложка еще не загружена' })
      } else {
        const albumSubmittedData = getAlbumRequestByFormValues(values)
        if (album.data) {
          void updateAlbum.start({
            id: album.data.id,
            data: albumSubmittedData,
          })
        } else {
          void createAlbum.start({ data: albumSubmittedData })
        }
      }
      setSubmitting(false)
    },
  })

  const { isValid, isValidating, isSubmitting, values } = formik
  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, isValid, isValidating])

  const onAuthorCreated = (id: string) => {
    void formik.setFieldValue('authorId', id)
  }
  const onCoauthorCreated = (id: string) => {
    void formik.setFieldValue('coauthorIds', [
      ...(formik.values.coauthorIds ?? []),
      id,
    ])
  }

  const handleAuthorsSelectPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      authorsPageIncreased()
    }
  }

  const handleCoauthorsSelectPopupScroll = (
    e: React.UIEvent<HTMLDivElement>,
  ) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      coauthorsPageIncreased()
    }
  }

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) || submitting

  return (
    <>
      <AlbumCommonInformationFormGate initialAuthor={initialAuthor} />

      <HidingSpin
        spinning={
          albumQueryStatus !== 'initial' &&
          (formDataPending || (!album.data && !album.error))
        }
      >
        <div style={{ maxWidth: 600 }}>
          <Form.Item required layout="vertical" label="Название">
            <SelfValidateInput
              name="name"
              showCount
              maxLength={MAX_FIELD_LENGTH.name}
              value={formik.values.name}
              onChange={(value) => {
                void formik.setFieldValue('name', value)
              }}
            />
          </Form.Item>
          <Form.Item
            required
            label="Автор"
            style={{ marginBottom: '8px' }}
            layout="vertical"
            tooltip={
              album.data
                ? 'Автора можно указать только при создании альбома'
                : null
            }
          >
            <Space style={{ width: '100%' }} direction="vertical">
              <Flex gap={4}>
                <Select
                  showSearch
                  disabled={Boolean(album.data) || Boolean(initialAuthor)}
                  style={{ width: '100%' }}
                  loading={authors.pending}
                  searchValue={authorSearchQuery || ''}
                  optionFilterProp="label"
                  value={formik.values.authorId}
                  onChange={(value: string) => {
                    void formik.setFieldValue('authorId', value)
                    resetAuthorsSearch()
                  }}
                  onSearch={(value: string) => {
                    changeAuthorsSearch(value)
                  }}
                  notFoundContent={
                    authors.pending ? <Spin size="small" /> : null
                  }
                  onOpenChange={(open) => {
                    if (!open) {
                      resetAuthorsSearch()
                    }
                  }}
                  onPopupScroll={handleAuthorsSelectPopupScroll}
                >
                  {authorsData
                    .filter((el) => !formik.values.coauthorIds?.includes(el.id))
                    .map((el) => (
                      <Select.Option
                        key={el.id}
                        value={el.id}
                        label={el.nickname}
                      >
                        {el.nickname}
                      </Select.Option>
                    ))}
                </Select>
                <CopyButton
                  getText={() => {
                    const selected = authorsData.find(
                      (el) => el.id === values.authorId,
                    )
                    return selected?.nickname ?? null
                  }}
                />
              </Flex>
              {!album.data && !initialAuthor && (
                <Button
                  onClick={() => {
                    onAuthorButtonClick(onAuthorCreated)
                  }}
                  icon={<PlusOutlined />}
                >
                  Добавить
                </Button>
              )}
            </Space>
          </Form.Item>

          {formik.values.authorId && (
            <Form.Item label="Соавторы" layout="vertical">
              <Space style={{ width: '100%' }} direction="vertical">
                <Flex gap={4}>
                  <Select
                    mode="multiple"
                    showSearch
                    value={formik.values.coauthorIds}
                    searchValue={coauthorSearch || ''}
                    style={{ width: '100%' }}
                    loading={coauthors.pending}
                    optionFilterProp="label"
                    onChange={(value: string[]) => {
                      void formik.setFieldValue('coauthorIds', value)
                      resetCoauthorsSearch()
                    }}
                    onSearch={(value: string) => {
                      changeCoauthorsSearch(value)
                    }}
                    notFoundContent={
                      authors.pending ? <Spin size="small" /> : null
                    }
                    onOpenChange={(open) => {
                      if (!open) {
                        resetCoauthorsSearch()
                      }
                    }}
                    onPopupScroll={handleCoauthorsSelectPopupScroll}
                  >
                    {coauthorsData
                      .filter((el) => el.id !== formik.values.authorId)
                      .map((el) => (
                        <Select.Option
                          key={el.id}
                          value={el.id}
                          label={el.nickname}
                        >
                          {el.nickname}
                        </Select.Option>
                      ))}
                  </Select>
                  <CopyButton
                    getText={() => {
                      const ids = values.coauthorIds
                      if (!ids || ids.length === 0) return null
                      const selected = ids
                        .map(
                          (id) =>
                            coauthorsData.find((el) => el.id === id)?.nickname,
                        )
                        .filter((name): name is string => Boolean(name))
                      if (selected.length === 0) return null
                      return selected.join(', ')
                    }}
                  />
                </Flex>
                <Button
                  onClick={() => {
                    onAuthorButtonClick(onCoauthorCreated)
                  }}
                  icon={<PlusOutlined />}
                >
                  Добавить
                </Button>
              </Space>
            </Form.Item>
          )}

          <Form.Item
            required
            label="Тип альбома"
            layout="vertical"
            tooltip={
              album.data
                ? 'Тип альбома можно указать только при создании альбома'
                : null
            }
          >
            <Select
              disabled={Boolean(album.data)}
              showSearch
              value={formik.values.type}
              onChange={(value) => void formik.setFieldValue('type', value)}
              options={albumTypeArray.map((albumType) => ({
                value: albumType,
                label: getAlbumTypeString(albumType),
              }))}
            />
          </Form.Item>

          <Form.Item required label="Дата выпуска" layout="vertical">
            <Flex gap={8}>
              <DatePicker
                inputReadOnly
                picker="month"
                placeholder={''}
                value={formik.values.releaseDate}
                onChange={(value) => {
                  const newReleaseDate = value
                    ? dayjs(value).startOf('month')
                    : null
                  void formik.setFieldValue('releaseDate', newReleaseDate)
                }}
              />
            </Flex>
          </Form.Item>

          <Form.Item required layout="vertical" label="Обложка">
            <UploadPicture
              value={formik.values.pictureFile || undefined}
              onChange={(fileList) => {
                const file = fileList[0] ?? null
                void formik.setFieldValue('pictureFile', file)
              }}
              onReset={() => {
                void formik.setFieldValue('pictureFile', null)
              }}
            />
          </Form.Item>

          <Form.Item>
            <Checkbox
              checked={formik.values.excludeSearch}
              onChange={(e) =>
                void formik.setFieldValue('excludeSearch', e.target.checked)
              }
            >
              Исключить из поиска
            </Checkbox>
          </Form.Item>

          <Space>
            {!album.data && (
              <Button
                disabled={formControlButtonsDisabled}
                onClick={() => formik.resetForm()}
              >
                Очистить
              </Button>
            )}
            <PrimaryButton
              disabled={formControlButtonsDisabled}
              loading={submitting}
              onClick={() => {
                formik.handleSubmit()
              }}
            >
              {album.data ? 'Сохранить' : 'Продолжить'}
            </PrimaryButton>
          </Space>
        </div>
      </HidingSpin>
    </>
  )
}
